import * as React from 'react';
import { useCallback } from 'react';

import { Box, HStack } from '@rbilabs/universal-components';

import { A11yWrapper } from 'components/a11y-wrapper';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { primitive } from 'styles/constants';
import { isNativeIOS } from 'utils/environment';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { StoreOrderButton } from '../store-actions/components';

import { StoreCardActions, StoreCardDetails } from './components';
import { Container, Distance } from './store-card.styled';
import { StoreCardViewProps } from './types';

export const StoreCardView: React.FC<StoreCardViewProps> = ({
  tab,
  isFavorite = false,
  isUpdatingFavorites = false,
  onCardPress,
  onDiagnosticsClick,
  onFavoriteClick,
  onOrderPress,
  onInfoPress,
  storeDetails,
  errorMessage,
  isInMapView,
  isInSupportForm,
  isSelected,
  isLastItem,
}) => {
  const {
    address,
    distanceText,
    restaurant,
    restaurantStatus: { canBeSelected, text: storeStatusText },
    isHalal,
    city,
    accessInstructions,
  } = storeDetails;
  const { modalAuthState } = useAuthContext();

  const isFavoriteSearch = modalAuthState.screen === ModalAuthScreen.SIGN_UP;
  const halalTitle = isHalal ? t('isHalalTitle') : '';
  const accessibilityLabel = `${address} ${city} ${storeStatusText}, ${halalTitle}, ${accessInstructions}`;

  const handleOnPress = () => {
    if (!isSelected) {
      onCardPress?.();
    }
  };

  const onLayout = useCallback(() => {
    if (tab === OrderLocatorTab.NEARBY) {
      // the 1st store that hits will stop the trace, any more don't matter as trace is done.
      stopTrace(TraceName.STORE_LOCATOR_AUTO_NEARBY_LOCATION_SEARCH_TO_LIST_ITEM_DISPLAYED);
      stopTrace(TraceName.STORE_LOCATOR_RESTAURANT_ALREADY_SELECTED_TO_LIST_ITEM_DISPLAYED);
      stopTrace(TraceName.STORE_LOCATOR_MANUAL_SEARCH_LOCATION_PRESSED_TO_LIST_ITEM_DISPLAYED);
    }
  }, [tab]);

  return (
    <Container
      onLayout={onLayout}
      onPress={handleOnPress}
      accessible={!isSelected}
      accessibilityState={{ expanded: !!isSelected }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      dd-action-name={`${storeDetails.address} open accordion`}
      aria-expanded={isSelected}
      backgroundColor={!canBeSelected ? primitive.$blackOpacity4 : undefined}
      paddingBottom={isNativeIOS() && isLastItem ? '$7' : undefined}
    >
      <HStack>
        <StoreCardDetails
          errorMessage={errorMessage}
          storeDetails={storeDetails}
          isInMapView={isInMapView}
          distanceComponent={
            <Box alignSelf={'flex-end'}>
              <A11yWrapper flexGrow={0}>
                <Distance>{distanceText}</Distance>
              </A11yWrapper>
            </Box>
          }
        />
      </HStack>
      <HStack>
        {(isSelected || isInMapView) && (
          <StoreCardActions
            dd-action-name={`${address} ${isFavorite ? 'unfavorite' : 'favorite'}`}
            isFavorite={isFavorite}
            canBeSelected={canBeSelected}
            onDiagnosticsClick={!isFavoriteSearch ? onDiagnosticsClick : undefined}
            onFavoritesClick={!isFavoriteSearch ? onFavoriteClick : onCardPress}
            onInfoPress={onInfoPress}
            isUpdatingFavorites={isUpdatingFavorites}
            containerStyle={{
              flex: onDiagnosticsClick ? 2 : 1,
              paddingTop: '$7',
              justifyContent: 'space-around',
              paddingRight: '$6',
            }}
          />
        )}
        <Box flex={5}>
          {isSelected && onInfoPress && !canBeSelected && (
            <ActionButton
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              onPress={onInfoPress}
              alignSelf={'center'}
              marginTop={'$5'}
              borderColor={primitive.$bbqBrown}
              _text={{ color: primitive.$bbqBrown }}
            >
              {t('viewStoreHours')}
            </ActionButton>
          )}
          {(isSelected || isInMapView) && canBeSelected && (
            <StoreOrderButton
              isInSupportForm={isInSupportForm}
              onOrderPress={onOrderPress}
              restaurant={restaurant}
              disabled={!canBeSelected}
            />
          )}
        </Box>
      </HStack>
    </Container>
  );
};
